import { useState } from 'react';
import { Sliders } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { matchWeightsSchema } from '@/lib/validation';

interface MatchWeights {
  amount: number;
  date: number;
  narration: number;
}

interface Props {
  weights: MatchWeights;
  onSave: (weights: MatchWeights) => void;
}

export function MatchWeightsConfig({ weights, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [localWeights, setLocalWeights] = useState(weights);
  const { toast } = useToast();

  const handleSave = () => {
    // Validate using Zod schema
    const result = matchWeightsSchema.safeParse(localWeights);
    
    if (!result.success) {
      toast({
        variant: 'destructive',
        title: 'Invalid Weights',
        description: result.error.issues[0]?.message || 'Weights must sum to 1.0'
      });
      return;
    }

    onSave(localWeights);
    setOpen(false);
    toast({
      title: 'Settings Saved',
      description: 'Match weights have been updated'
    });
  };

  const handleWeightChange = (key: keyof MatchWeights, value: number) => {
    const newWeights = { ...localWeights, [key]: value };
    setLocalWeights(newWeights);
  };

  const normalize = () => {
    const sum = localWeights.amount + localWeights.date + localWeights.narration;
    if (sum === 0) return;
    
    setLocalWeights({
      amount: localWeights.amount / sum,
      date: localWeights.date / sum,
      narration: localWeights.narration / sum
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Sliders size={16} />
          <span className="hidden sm:inline">Configure</span>
          <span className="sm:hidden">Config</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Match Algorithm Settings</DialogTitle>
          <DialogDescription>
            Adjust weights for matching criteria (must sum to 1.0)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Amount Matching</Label>
              <span className="text-sm text-muted-foreground">
                {(localWeights.amount * 100).toFixed(0)}%
              </span>
            </div>
            <Slider
              value={[localWeights.amount * 100]}
              onValueChange={([value]) => handleWeightChange('amount', value / 100)}
              max={100}
              step={1}
            />
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Date Matching</Label>
              <span className="text-sm text-muted-foreground">
                {(localWeights.date * 100).toFixed(0)}%
              </span>
            </div>
            <Slider
              value={[localWeights.date * 100]}
              onValueChange={([value]) => handleWeightChange('date', value / 100)}
              max={100}
              step={1}
            />
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <Label>Text Matching</Label>
              <span className="text-sm text-muted-foreground">
                {(localWeights.narration * 100).toFixed(0)}%
              </span>
            </div>
            <Slider
              value={[localWeights.narration * 100]}
              onValueChange={([value]) => handleWeightChange('narration', value / 100)}
              max={100}
              step={1}
            />
          </div>

          <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">Total:</span>
            <span className={`text-sm font-bold ${
              Math.abs((localWeights.amount + localWeights.date + localWeights.narration) - 1.0) < 0.01
                ? 'text-success'
                : 'text-destructive'
            }`}>
              {((localWeights.amount + localWeights.date + localWeights.narration) * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={normalize}
            className="flex-1"
          >
            Normalize
          </Button>
          <Button
            onClick={handleSave}
            className="flex-1"
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
